/**
 * The text form of a date field: DD/MM/YYYY (the app's locale, `appConfig.locale`), typed as digits with the slashes
 * put in as you go. The value everyone outside the field sees is an ISO date, "YYYY-MM-DD", or "" when the text is
 * not a real date yet.
 */

const SEPARATOR = "/";
const DAY_DIGITS = 2;
const MONTH_DIGITS = 2;
const YEAR_DIGITS = 4;
const MAX_DIGITS = DAY_DIGITS + MONTH_DIGITS + YEAR_DIGITS;
const MONTHS_IN_YEAR = 12;

/** "1204202" typed so far becomes "12/04/202": digits only, at most eight, slashes after the day and month. */
export function maskDate(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, MAX_DIGITS);
  const day = digits.slice(0, DAY_DIGITS);
  const month = digits.slice(DAY_DIGITS, DAY_DIGITS + MONTH_DIGITS);
  const year = digits.slice(DAY_DIGITS + MONTH_DIGITS);
  return [day, month, year].filter((part) => part !== "").join(SEPARATOR);
}

/** "2026-10-30" shown as "30/10/2026"; anything else shows as nothing. */
export function isoToDisplay(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match === null
    ? ""
    : `${match[3] ?? ""}${SEPARATOR}${match[2] ?? ""}${SEPARATOR}${match[1] ?? ""}`;
}

/** "30/02/2026" is not a date (undefined); "30/10/2026" is "2026-10-30". Incomplete text is undefined too. */
export function displayToIso(text: string): string | undefined {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  if (match === null) return undefined;
  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > MONTHS_IN_YEAR || day < 1) return undefined;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day > daysInMonth) return undefined;
  const pad = (value: number, width: number): string => String(value).padStart(width, "0");
  return `${pad(year, YEAR_DIGITS)}-${pad(month, MONTH_DIGITS)}-${pad(day, DAY_DIGITS)}`;
}

/** The placeholder that tells people the order. */
export const DATE_PLACEHOLDER = "DD/MM/YYYY";
